import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Heart, Info, MapPin, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { areaKey, etykietaObszaru, type SearchArea } from '@/lib/areaSearch';
import { dzielnicaObszaru, usunObszar } from '@/lib/areaSelection';
import { WyborDzielnic } from './WyborDzielnic';
import { klasyZastapienia, useBlyskZastapienia } from '@/hooks/useBlyskZastapienia';
import { BliskoDo } from './BliskoDo';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useDotyk } from '@/hooks/use-mobile';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { domyslnieOtwarta, podsumowanieSekcji, wczytajOtwarte, zapiszOtwarte, type PodsumowanieSekcji, type SekcjaPanelu } from '@/lib/sekcjePanelu';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { formatLiczby } from '@/lib/i18nNumber';
import { LICZBA_KROKOW, filtrZSuwaka, kwotaZPola, kwotaZPozycji, tekstPolaKwoty, uporzadkujZakres, wartoscSuwaka } from '@/lib/skalaCeny';
import { HistogramCen } from './HistogramCen';
import { useHistogramCen } from '@/hooks/useHistogramCen';
import { usePhotoSourceTags, setPhotoSourceTags } from '@/lib/debugFlags';
import { canSeePortalData } from '@/lib/portalVisibility';
import { TYPY_BUDYNKU, UDOGODNIENIA, WIEKI_BUDYNKU } from '@/lib/filtryMiekkie';
import { POSTED_OPTIONS } from '@/lib/serpUrl';
import { WarsawMetroIcon } from '@/components/offer/WarsawMetroBadge';
import { IKONA_FILTRA_POI, IkonaOdZaraz, IkonaZwierzat } from '@/components/search/IkonyFiltrowOkolicy';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Pola panelu, które da się pokazać osobno. Profil („Moje wyszukiwanie", 30.09) renderuje TEN SAM
 * panel, tylko z polami, które profil przechowuje — jedna kontrolka, jeden wygląd, zero kopii.
 */
export type PoleFiltra =
  | 'lokalizacja'
  | 'cena' | 'pokoje' | 'metraz'
  | 'umeblowanie' | 'pietro' | 'kuchnia' | 'lazienki' | 'balkon' | 'winda' | 'internet'
  | 'metro' | 'okolica' | 'zwierze'
  | 'dostepnosc' | 'zdjecia' | 'budynek' | 'udogodnienia' | 'kaucja' | 'debug';

interface Props {
  filters: SourcePostFilters;
  onChange: (next: SourcePostFilters) => void;
  /** Brak = wszystkie pola (wyszukiwarka). Podane = tylko te, sekcje bez pól znikają. */
  pola?: readonly PoleFiltra[];
  /** Podpis pod kontrolką (profil: „włącza się samo, gdy dodasz zwierzę”). Tylko `zwierze` dziś. */
  podpisy?: Partial<Record<'zwierze', string>>;
  /**
   * Wiersze zwijane z podsumowaniem i ⓘ tylko na otwartym (wyszukiwarka, plan E3.1). Profil
   * zostaje przy płaskich sekcjach do czasu własnej przebudowy (E6).
   */
  zwijane?: boolean;
}

// Godziny, bo tak jedzie filtr w adresie i w predykacie (`postedSinceHours`). 720 = 30 dni,
// 2160 = 90 dni — dwie dłuższe opcje dołożone 27.09: mediana czasu na rynku to 56 dni, więc
// „ostatni tydzień" odcinał większość katalogu i nie było jak spojrzeć na coś pomiędzy
// tygodniem a „dowolnie".
// Lista żyje w serpUrl: adres przyjmuje tylko te wartości (każda ma etykietę `posted_options.*`).

// STATIC slider scales (qasa-style — no interdependency with other filters or the
// live inventory). A thumb parked at an extreme = open-ended on that side, stored
// as undefined in the filter (so pill/chip/slider caption all read "od X" / "do Y"
// / "X–Y" with NO ∞/"+" symbol — see formatRange). Ceilings sit above the real
// data max (~10 000 zł / ~337 m²); offers beyond a parked thumb are still shown.
// Cena: skala 1 000–8 000+ (gęstsza w środku) i jej reguły siedzą w `src/lib/skalaCeny.ts`.
const AREA_SLIDER_MIN = 0;
const AREA_SLIDER_MAX = 250;
const AREA_SLIDER_STEP = 1;

/**
 * Price control — cost-mode segment + range slider. Extracted so it can be
 * reused both in the full filter panel and in the desktop top-bar "Cena" quick
 * popover. Owns its own slider draft: the live thumb + caption follow the draft
 * WHILE dragging; the filter only fires on release (onValueCommit). The draft
 * resyncs when the committed value changes externally (reset).
 */
export function PriceControl({
  filters,
  onChange,
  /** W arkuszu tytuł niesie nagłówek sekcji — druga etykieta byłaby powtórzeniem. */
  bezEtykiety = false,
}: {
  filters: SourcePostFilters;
  onChange: (next: SourcePostFilters) => void;
  bezEtykiety?: boolean;
}) {
  const { t, i18n } = useTranslation();
  /**
   * DWA STANY ROBOCZE: pozycje uchwytów (0…LICZBA_KROKOW, krok 1) i kwoty. Osobno, bo kwota
   * wpisana w pole (2 950 zł) stawia uchwyt MIĘDZY krokami, a przeliczenie pozycji z powrotem
   * na kwotę zaokrągliłoby ją do kroku. Oba idą za filtrem, gdy zmieni się z zewnątrz
   * (wyczyszczenie, stary adres).
   */
  const priceValue = wartoscSuwaka(filters.rentMin, filters.rentMax);
  const [priceDraft, setPriceDraft] = useState<[number, number]>(priceValue);
  const [kwotyDraft, setKwotyDraft] = useState<{ rentMin?: number; rentMax?: number }>({
    rentMin: filters.rentMin,
    rentMax: filters.rentMax,
  });
  useEffect(() => {
    setPriceDraft(wartoscSuwaka(filters.rentMin, filters.rentMax));
    setKwotyDraft({ rentMin: filters.rentMin, rentMax: filters.rentMax });
  }, [filters.rentMin, filters.rentMax]);

  /**
   * Histogram liczy BAZA i tym samym predykatem, co licznik „Pokaż N ofert" — patrz
   * `useHistogramCen`. Zapytanie leci raz na zmianę INNEGO filtra (klucz pomija cenę), więc
   * ruch suwaka nic nie pobiera. Gdy funkcji nie ma jeszcze w bazie, `niedostepny` jest
   * prawdą i zostaje sam suwak, bez błędu i bez pustej ramki.
   */
  const { data: histogram } = useHistogramCen(filters);

  const zatwierdzPola = (od: number | null, doKwoty: number | null) =>
    onChange({ ...filters, ...uporzadkujZakres(od, doKwoty) });

  return (
    <div className="space-y-1.5">
      {/* Price: mode segment (advertised rent vs rent + known utilities) above
          the range slider — switching the mode keeps the selected range. */}
      {!bezEtykiety && <Label>{t('search.filters.price')}</Label>}
      <div className="flex items-center gap-2">
      <div className="grid flex-1 grid-cols-2 gap-1 rounded-lg border bg-muted/30 p-0.5">
        {(['rent', 'total'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => onChange({ ...filters, costMode: mode === 'rent' ? undefined : mode })}
            // Pierścień TYLKO przy klawiaturze (`focus-visible`) i z tokenu `ring` (Konwencja 19);
            // domyślny obrys przeglądarki rysował się też przy dotyku.
            className={`strefa-dotyku relative h-9 rounded-md px-2 text-base font-medium outline-none md:h-8 md:text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
              (filters.costMode ?? 'rent') === mode
                ? 'bg-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`search.filters.cost_mode_${mode}`)}
          </button>
        ))}
      </div>
        {/* CO ZAWIERA KAŻDA OPCJA (Maciek przez Piotra 29.09). Popover, nie Tooltip — dotyk.
            Treść wyprowadzona z `flat.project_offers` (`total_monthly_cost_pln`) i reguły
            SRCH-15 w `applySerpFilters`, nie z pamięci: koszt = czynsz najmu + czynsz adm.
            (podany → nasz szacunek z dzielnicy i metrażu → 0), „opłaty w cenie” = +0; bez
            mediów rozliczanych osobno, parkingu i kaucji; bez podanych opłat i bez szacunku
            oferta wypada z filtra kosztu. */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="cena-koszt-info"
              aria-label={t('search.filters.cost_mode_info_label')}
              className="strefa-dotyku relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="end" collisionPadding={16} className="w-72 space-y-2 p-3 text-sm leading-relaxed text-muted-foreground md:text-xs">
            <p>
              <span className="font-medium text-foreground">{t('search.filters.cost_mode_rent')}</span>
              {' – '}
              {t('search.filters.cost_mode_rent_explain')}
            </p>
            <p>
              <span className="font-medium text-foreground">{t('search.filters.cost_mode_total')}</span>
              {' – '}
              {t('search.filters.cost_mode_total_explain')}
            </p>
            <p>{t('search.filters.cost_mode_total_unknown')}</p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="px-1 pt-3">
        {!histogram?.niedostepny && (
          // `mx-2.5` = połowa uchwytu (h-5 w-5 → 10 px). Radix trzyma uchwyt W torze, więc
          // jego środek chodzi od 10 px do szerokości toru minus 10 px, a nie od krawędzi
          // do krawędzi (zmierzone 28.09: bez wcięcia uchwyt stał 3–7 px obok swojej kwoty).
          // MIEJSCE ZAREZERWOWANE (h-12 = wysokość słupków): kontener stoi także w trakcie
          // pierwszego pobrania, więc sekcje pod nim nie skaczą o 52 px (pomiar 29.09). Zwija się
          // tylko wtedy, gdy funkcji histogramu w bazie nie ma wcale.
          <div className="mx-2.5 mb-1 h-12" data-testid="histogram-miejsce">
            {histogram && <HistogramCen koszyki={histogram.koszyki} rentMin={kwotyDraft.rentMin} rentMax={kwotyDraft.rentMax} />}
          </div>
        )}
        {/* Uchwyty przeskakują po granicach koszyków (krok = jeden słupek), więc przy
            suwaku suma zaznaczonych słupków zgadza się z listą; dowolną kwotę wpisuje się
            w pola niżej. */}
        <Slider
          min={0}
          max={LICZBA_KROKOW}
          step={1}
          value={priceDraft}
          onValueChange={(v) => {
            const [lo, hi] = v as [number, number];
            setPriceDraft([lo, hi]);
            setKwotyDraft(filtrZSuwaka(lo, hi));
          }}
          onValueCommit={([lo, hi]) => onChange({ ...filters, ...filtrZSuwaka(lo, hi) })}
          // Czytnik ekranu dostaje PRAWDZIWĄ kwotę (także wpisaną w pole, np. 2 950), a nie
          // granicę kroku, na której akurat stoi uchwyt.
          tekstWartosci={(p, i) => {
            const kwota = i === 0 ? kwotyDraft.rentMin : kwotyDraft.rentMax;
            if (kwota == null) return i === 0 ? `${formatLiczby(kwotaZPozycji(0), i18n.language)} zł` : `${formatLiczby(kwotaZPozycji(LICZBA_KROKOW), i18n.language)}+ zł`;
            return `${formatLiczby(kwota, i18n.language)} zł`;
          }}
          dymek={(p, i) => {
            const kwota = i === 0 ? kwotyDraft.rentMin : kwotyDraft.rentMax;
            return kwota == null ? t('search.filters.price_no_limit') : `${formatLiczby(kwota, i18n.language)} zł`;
          }}
          aria-label={t('search.filters.price')}
        />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <PoleKwoty
            etykieta={t('search.filters.price_from')}
            kwota={kwotyDraft.rentMin}
            podpowiedz={t('search.filters.price_no_limit')}
            jezyk={i18n.language}
            testId="pole-cena-od"
            naZatwierdz={(v) => zatwierdzPola(v, filters.rentMax ?? null)}
          />
          <PoleKwoty
            etykieta={t('search.filters.price_to')}
            kwota={kwotyDraft.rentMax}
            podpowiedz={t('search.filters.price_no_limit')}
            jezyk={i18n.language}
            testId="pole-cena-do"
            naZatwierdz={(v) => zatwierdzPola(filters.rentMin ?? null, v)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Pole kwoty „od / do" pod suwakiem. Tekst w trakcie pisania jest lokalny; poza fokusem
 * pole pokazuje kwotę z filtra (albo z uchwytu, gdy się go przeciąga) w formacie z odstępem
 * tysięcy. Zatwierdzenie przy opuszczeniu pola i Enterze; puste = bez granicy.
 * `text-base` (16 px) — poniżej iPhone powiększa stronę (bramka mobileInputZoom).
 */
function PoleKwoty({
  etykieta,
  kwota,
  podpowiedz,
  jezyk,
  testId,
  naZatwierdz,
  jednostka = 'zł',
}: {
  etykieta: string;
  kwota: number | undefined;
  podpowiedz: string;
  jezyk: string;
  testId: string;
  naZatwierdz: (v: number | null) => void;
  /** „zł” pod ceną, „m²” pod metrażem — to samo pole, inna jednostka. */
  jednostka?: string;
}) {
  const [tekst, setTekst] = useState<string | null>(null);
  const pokazywany = tekst ?? (kwota != null ? formatLiczby(kwota, jezyk) : '');
  const zatwierdz = () => {
    if (tekst == null) return;
    const v = kwotaZPola(tekst);
    setTekst(null);
    if (v !== (kwota ?? null)) naZatwierdz(v);
  };
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-muted-foreground md:text-xs">{etykieta}</span>
      {/* Ramka ma wysokość pigułki (40 / 36 px): pole i wybór to jedna skala (Piotr 29.09). */}
      <span className="flex h-10 items-center rounded-lg border bg-white px-3 focus-within:ring-2 focus-within:ring-ring md:h-9">
        <input
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          // Nierozpoznany token + unikalna nazwa zamiast "off": Chrome potrafi zignorować "off"
          // i podsunąć w pole kwoty tekst wpisany kiedyś gdzie indziej (29.09: „kawalerka”).
          name={`flh-${testId}`}
          autoComplete={`flh-${testId}`}
          data-testid={testId}
          value={pokazywany}
          placeholder={podpowiedz}
          onFocus={() => setTekst(pokazywany)}
          onChange={(e) => setTekst(tekstPolaKwoty(e.target.value))}
          onBlur={zatwierdz}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          className="h-full w-full min-w-0 bg-transparent text-base text-muted-foreground outline-none placeholder:text-muted-foreground focus:text-foreground md:text-sm"
        />
        <span className="shrink-0 pl-1 text-base text-muted-foreground md:text-sm">{jednostka}</span>
      </span>
    </label>
  );
}

/** Rooms multi-toggle (1/2/3/4+). Reused in the panel and the top-bar popover. */
export function RoomsControl({
  filters,
  onChange,
  bezEtykiety = false,
  bezNotki = false,
}: {
  filters: SourcePostFilters;
  onChange: (next: SourcePostFilters) => void;
  bezEtykiety?: boolean;
  /** W panelu notka jest pod ⓘ nagłówka sekcji; w okienku górnego paska zostaje pod pigułkami. */
  bezNotki?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      {!bezEtykiety && <Label>{t('search.filters.rooms')}</Label>}
      {/* Pigułki jak każda inna grupa wyboru (Piotr 29.09), z „Dowolne” na początku jak u
          Airbnb. Wybór wielokrotny: „Dowolne” zdejmuje wszystkie. Wartości dalej 1–4
          (skala „Kawalerka · 2 · 3 · 4+”, R 28.09). */}
      {/* Odstęp 4 px na telefonie: pięć pigułek w 16 px mieści się wtedy w jednym rzędzie
          (327 px szerokości panelu na 375) — przy 6 px „4+” spadało do drugiej linii. */}
      <div className="flex flex-wrap gap-1 md:gap-1.5">
        <FilterChip
          active={!filters.rooms?.length}
          label={t('search.filters.any_n')}
          onToggle={() => onChange({ ...filters, rooms: undefined })}
        />
        {[1, 2, 3, 4].map((r) => {
          const active = filters.rooms?.includes(r) ?? false;
          return (
            <FilterChip
              key={r}
              active={active}
              label={r === 1 ? t('search.filters.rooms_studio') : r === 4 ? t('search.filters.rooms_4plus') : String(r)}
              onToggle={() => {
                const current = filters.rooms ?? [];
                const next = active ? current.filter((x) => x !== r) : [...current, r];
                onChange({ ...filters, rooms: next.length ? next : undefined });
              }}
            />
          );
        })}
      </div>
      {!bezNotki && <p className="text-sm text-muted-foreground md:text-xs">{t('search.filters.coverage_hint')}</p>}
    </div>
  );
}

/**
 * WARTOŚĆ ZAKRESU PRZY TYTULE (Piotr 30.09, telefon): „Metraż (m²) · 40–bez limitu”. Widoczna
 * zawsze i na żywo w trakcie przeciągania, bo przy dłuższym panelu pola Od/Do bywają pod
 * ekranem albo pod palcem. Słownictwo pól: pusta granica = „bez limitu”, obie puste = „Dowolny”.
 * `tabular-nums` + `whitespace-nowrap`: cyfry mają stałą szerokość, a napis nie łamie się na
 * dwie linie, więc przesuwanie uchwytu nie zmienia wysokości nagłówka (bramka panel-przesuniecia).
 */
export function WartoscZakresu({ od, do: doWartosci, format }: { od?: number; do?: number; format: (v: number) => string }) {
  const { t } = useTranslation();
  const tekst = od == null && doWartosci == null
    ? t('search.filters.range_any')
    : `${od != null ? format(od) : t('search.filters.price_no_limit')}–${doWartosci != null ? format(doWartosci) : t('search.filters.price_no_limit')}`;
  return (
    <span data-testid="wartosc-zakresu" className="whitespace-nowrap text-base font-normal tabular-nums text-muted-foreground md:text-sm">
      {' · '}{tekst}
    </span>
  );
}

/**
 * Metraż: suwak + pola Od/Do jak przy cenie (30.09). Skala stała 0–250 m²; uchwyt na krańcu =
 * bez granicy z tej strony. Pole przyjmuje też wartość spoza skali (300 m²) — wtedy uchwyt stoi
 * na krańcu, a nagłówek i pole pokazują wpisaną liczbę, nie krawędź skali.
 * `tytulSekcji` (panel „Filtry”): kontrolka sama rysuje nagłówek sekcji, bo wartość przy tytule
 * idzie za uchwytem W TRAKCIE przeciągania, a ten stan żyje tutaj.
 */
export function AreaControl({
  filters,
  onChange,
  bezEtykiety = false,
  tytulSekcji,
}: {
  filters: SourcePostFilters;
  onChange: (next: SourcePostFilters) => void;
  bezEtykiety?: boolean;
  tytulSekcji?: string;
}) {
  const { t, i18n } = useTranslation();
  const suwakZ = (od?: number, doW?: number): [number, number] => [
    Math.min(od ?? AREA_SLIDER_MIN, AREA_SLIDER_MAX),
    Math.min(doW ?? AREA_SLIDER_MAX, AREA_SLIDER_MAX),
  ];
  const [areaDraft, setAreaDraft] = useState<[number, number]>(suwakZ(filters.areaMin, filters.areaMax));
  const [wartosci, setWartosci] = useState<{ areaMin?: number; areaMax?: number }>({ areaMin: filters.areaMin, areaMax: filters.areaMax });
  useEffect(() => {
    setAreaDraft(suwakZ(filters.areaMin, filters.areaMax));
    setWartosci({ areaMin: filters.areaMin, areaMax: filters.areaMax });
  }, [filters.areaMin, filters.areaMax]);

  const zSuwaka = (lo: number, hi: number) => ({
    areaMin: lo > AREA_SLIDER_MIN ? lo : undefined,
    areaMax: hi < AREA_SLIDER_MAX ? hi : undefined,
  });
  const zatwierdzPola = (od: number | null, doW: number | null) => {
    const [a, b] = od != null && doW != null && od > doW ? [doW, od] : [od, doW];
    onChange({ ...filters, areaMin: a ?? undefined, areaMax: b ?? undefined });
  };
  const m2 = (v: number) => `${formatLiczby(v, i18n.language)} m²`;
  const wartosc = <WartoscZakresu od={wartosci.areaMin} do={wartosci.areaMax} format={(v) => formatLiczby(v, i18n.language)} />;

  return (
    <div className="space-y-1.5">
      {tytulSekcji ? (
        <NaglowekSekcjiZInfo tytul={tytulSekcji} wartosc={wartosc} />
      ) : (
        !bezEtykiety && <Label>{t('search.filters.area')}{wartosc}</Label>
      )}
      {/* `pt-9`: miejsce na dymek nad uchwytem (32 px), żeby w trakcie przeciągania nie zasłaniał
          wartości przy tytule. Stała wysokość — dymek jest `absolute`, nic nie skacze. */}
      <div className="px-1 pt-9">
        <Slider
          min={AREA_SLIDER_MIN}
          max={AREA_SLIDER_MAX}
          step={AREA_SLIDER_STEP}
          value={areaDraft}
          onValueChange={(v) => {
            const [lo, hi] = v as [number, number];
            setAreaDraft([lo, hi]);
            setWartosci(zSuwaka(lo, hi));
          }}
          onValueCommit={([lo, hi]) => onChange({ ...filters, ...zSuwaka(lo, hi) })}
          tekstWartosci={(v, i) => {
            const w = i === 0 ? wartosci.areaMin : wartosci.areaMax;
            return w == null ? t('search.filters.price_no_limit') : m2(w);
          }}
          dymek={(v, i) => {
            const w = i === 0 ? wartosci.areaMin : wartosci.areaMax;
            return w == null ? t('search.filters.price_no_limit') : m2(w);
          }}
          aria-label={t('search.filters.area')}
        />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <PoleKwoty
            etykieta={t('search.filters.price_from')}
            kwota={wartosci.areaMin}
            podpowiedz={t('search.filters.price_no_limit')}
            jezyk={i18n.language}
            testId="pole-metraz-od"
            jednostka="m²"
            naZatwierdz={(v) => zatwierdzPola(v, filters.areaMax ?? null)}
          />
          <PoleKwoty
            etykieta={t('search.filters.price_to')}
            kwota={wartosci.areaMax}
            podpowiedz={t('search.filters.price_no_limit')}
            jezyk={i18n.language}
            testId="pole-metraz-do"
            jednostka="m²"
            naZatwierdz={(v) => zatwierdzPola(filters.areaMin ?? null, v)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Wiersz Lokalizacja (makieta `serp-filter-panel.html`): pastylka miasta, chipy wybranych
 * obszarów z ×, i link „Wybierz dzielnice” / „Zmień dzielnice” / „Zwiń listę”, który rozwija
 * kaskadę W MIEJSCU — a nie w osobnym oknie, bo panel na telefonie jest już pełnoekranowy.
 * Edytuje ten sam `areas`, co pole wyszukiwania: panel pracuje na szkicu, więc wybór wchodzi
 * z „Pokaż N ofert”, jak każdy inny filtr panelu. `suburbs` (stare pole) czyścimy jak wszędzie.
 */
export function LokalizacjaControl({ filters, onChange }: { filters: SourcePostFilters; onChange: (next: SourcePostFilters) => void }) {
  const { t } = useTranslation();
  const [listaOtwarta, setListaOtwarta] = useState(false);
  const areas = filters.areas ?? [];
  const zmien = (next: SearchArea[]) => onChange({ ...filters, areas: next.length ? next : undefined, suburbs: undefined });
  const miasto = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const osiedleDzielnica = (a: SearchArea) => (a.kind === 'subdistrict' ? dzielnicaObszaru(a) : null);
  // Podgląd i błysk zastąpienia jak w polu wyszukiwania (plan §1.91): najechanie na dzielnicę albo
  // osiedle w liście przekreśla chip, który zniknie; po zastąpieniu nowy chip krótko świeci.
  const [przygaszone, setPrzygaszone] = useState<string[]>([]);
  const blysk = useBlyskZastapienia(areas);

  return (
    <div className="space-y-3" data-testid="lokalizacja-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-10 items-center gap-1.5 rounded-full border border-dashed px-3 text-base font-medium text-muted-foreground md:h-9 md:text-sm">
          <MapPin className="h-4 w-4" />
          {miasto}
        </span>
        {areas.map((a) => {
          const label = etykietaObszaru(a, t);
          const rodzic = osiedleDzielnica(a);
          const przygaszony = przygaszone.includes(areaKey(a));
          const swieci = blysk === areaKey(a);
          return (
            <span
              key={areaKey(a)}
              data-testid="chip-lokalizacji"
              data-przygaszony={przygaszony || undefined}
              data-blysk={swieci || undefined}
              className={`inline-flex h-10 items-center gap-1 rounded-full border border-primary pl-3 pr-1 text-base font-medium text-foreground md:h-9 md:text-sm ${klasyZastapienia({
                przygaszony,
                blysk: swieci,
                tlo: 'bg-primary/5',
              })}`}
            >
              {a.kind === 'station' && <WarsawMetroIcon size={14} />}
              {label}
              {rodzic && <span className="font-normal text-muted-foreground">· {rodzic}</span>}
              <button
                type="button"
                aria-label={t('search.area.remove', { name: label })}
                onClick={() => zmien(usunObszar(areas, areaKey(a)))}
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          );
        })}
        <button
          type="button"
          data-testid="lokalizacja-zmien"
          aria-expanded={listaOtwarta}
          onClick={() => setListaOtwarta((v) => !v)}
          className="h-10 px-1 text-base font-medium text-primary underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring hover:underline md:h-9 md:text-sm"
        >
          {t(listaOtwarta ? 'search.area.collapse_list' : areas.some((a) => a.kind === 'district' || a.kind === 'subdistrict') ? 'search.area.change_districts' : 'search.area.pick_districts')}
        </button>
      </div>
      {listaOtwarta && (
        <>
          <WyborDzielnic areas={areas} onChange={zmien} onPodglad={setPrzygaszone} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setListaOtwarta(false)}
              className="h-10 px-2 text-base font-medium text-primary underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring hover:underline md:h-9 md:text-sm"
            >
              {t('search.area.done')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Price/Rooms are extracted (PriceControl/RoomsControl)
// and reused by the desktop top-bar quick popovers; the panel composes them.

/**
 * Nagłówek sekcji z opcjonalnym ⓘ (Popover — dotyk). Decyzja Piotra 29.09 („róbmy to”): WSZYSTKIE
 * wyjaśnienia panelu idą tutaj, a w panelu zostają same kontrolki. Przy okazji znika źródło
 * skoków: notki pod kontrolkami zawijały się różnie przy pierwszym renderze.
 * Wariant PŁASKI — profil („Moje wyszukiwanie”) i panel bez `zwijane`.
 */
function NaglowekSekcjiZInfo({ tytul, info, wartosc }: { tytul: string; info?: { etykieta: string; tresc: React.ReactNode }; wartosc?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <h3 className="text-lg font-semibold text-foreground md:text-base">{tytul}{wartosc}</h3>
      {info && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={info.etykieta}
              className="strefa-dotyku relative inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="start" collisionPadding={16} className="w-72 space-y-2 p-3 text-sm leading-relaxed text-muted-foreground md:text-xs">
            {info.tresc}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/**
 * ⓘ ZWIJANEGO WIERSZA (plan §1.25): dymek po najechaniu na pulpicie, po dotknięciu na telefonie
 * (dotknięcie gdziekolwiek indziej zamyka), nigdy stały tekst. Jeden Popover sterowany ręcznie,
 * bo Tooltip nie otwiera się dotykiem, a Popover nie otwiera się najechaniem — tu potrzebne są
 * oba, zależnie od GŁÓWNEGO wskaźnika (`useDotyk`, nie szerokość ekranu).
 * Klik nie dociera do wiersza (stopPropagation): ⓘ wyjaśnia, nie zwija.
 */
function InfoSekcji({ etykieta, tresc, testId }: { etykieta: string; tresc: React.ReactNode; testId: string }) {
  const [otwarte, setOtwarte] = useState(false);
  const dotyk = useDotyk();
  const najechanie = dotyk ? {} : { onPointerEnter: () => setOtwarte(true), onPointerLeave: () => setOtwarte(false) };
  return (
    <Popover open={otwarte} onOpenChange={setOtwarte}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={etykieta}
          data-testid={testId}
          // Dotyk: dotknięcie przełącza. Mysz: najechanie już otworzyło, więc klik tylko trzyma
          // dymek otwarty (klawiatura: Enter otwiera, Escape zamyka — Radix).
          onClick={(e) => { e.stopPropagation(); setOtwarte((v) => (dotyk ? !v : true)); }}
          {...najechanie}
          className="strefa-dotyku relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:text-foreground"
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        collisionPadding={16}
        onClick={(e) => e.stopPropagation()}
        {...najechanie}
        className="w-72 space-y-2 p-3 text-sm leading-relaxed text-muted-foreground md:text-xs"
      >
        {tresc}
      </PopoverContent>
    </Popover>
  );
}

interface SekcjaProps {
  id: SekcjaPanelu;
  /** Brak = nagłówek rysuje sama kontrolka (metraż w wariancie płaskim: wartość przy tytule idzie za uchwytem). */
  tytul?: string;
  info?: { etykieta: string; tresc: React.ReactNode };
  children: React.ReactNode;
  /** Wiersz zwijany (wyszukiwarka). Brak = płaska sekcja jak dotąd (profil). */
  zwijanie?: { otwarta: boolean; przelacz: () => void; podsumowanie: PodsumowanieSekcji };
}

/**
 * Sekcja panelu. Dwa warianty:
 *  - PŁASKA (profil, 30.09): nagłówek z ⓘ i kontrolki, bez zwijania;
 *  - ZWIJANA (wyszukiwarka, plan E3.1, makieta `serp-filter-panel.html`): zwinięty wiersz =
 *    tytuł · bieżąca wartość na zielono · strzałka, BEZ ⓘ; otwarty = ⓘ z dymkiem. Klik w cały
 *    wiersz otwiera i zamyka; klawiatura idzie przez przycisk tytułu (aria-expanded).
 *    Wartość stoi także na otwartym wierszu (makieta), więc przy przewijaniu długiej sekcji
 *    widać, co w niej ustawiono.
 */
function Sekcja({ id, tytul, info, children, zwijanie }: SekcjaProps) {
  if (!zwijanie) {
    return (
      <section data-sekcja={id} className="border-b py-5 first:pt-0 last:border-b-0">
        {tytul && <NaglowekSekcjiZInfo tytul={tytul} info={info} />}
        <div className="space-y-3">{children}</div>
      </section>
    );
  }
  const { otwarta, przelacz, podsumowanie } = zwijanie;
  const trescId = `sekcja-filtrow-${id}`;
  return (
    <section
      data-sekcja={id}
      data-otwarta={otwarta}
      className={cn(
        'border-b last:border-b-0',
        // Ramka diagnostyczna: w kwarantannie wizualnej (kreskowana, w kolorze ostrzeżenia), żeby
        // łatwo ją było znaleźć i usunąć przed startem (docs/PreLaunchChecklist.md 2.6b).
        id === 'debug' && 'mt-2 rounded-lg border border-dashed border-warning/60 bg-warning-soft/60 px-3 last:border-b',
      )}
    >
      {/* Klik w wiersz zwija/rozwija; przycisk tytułu i ⓘ zatrzymują propagację, żeby jeden klik
          nie przełączał dwa razy. */}
      <div
        role="presentation"
        onClick={przelacz}
        className="flex min-h-[52px] cursor-pointer select-none items-center gap-2 py-2"
      >
        <h3 className={cn('shrink-0 font-semibold text-foreground', id === 'debug' ? 'text-xs uppercase tracking-wide text-warning-soft-foreground' : 'text-base md:text-[15px]')}>
          <button
            type="button"
            aria-expanded={otwarta}
            aria-controls={trescId}
            onClick={(e) => { e.stopPropagation(); przelacz(); }}
            className="rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {tytul}
          </button>
        </h3>
        {otwarta && info && <InfoSekcji etykieta={info.etykieta} tresc={info.tresc} testId={`info-${id}`} />}
        <span
          data-testid={`podsumowanie-${id}`}
          title={podsumowanie.tekst}
          className={cn(
            'min-w-0 flex-1 truncate text-right text-sm',
            podsumowanie.ustawione ? 'font-medium text-primary' : 'text-muted-foreground',
            id === 'debug' && 'text-warning-soft-foreground',
          )}
        >
          {podsumowanie.tekst}
        </span>
        {/* Makieta: `transition: transform .18s ease`, bez ruchu przy prefers-reduced-motion. */}
        <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-180 ease-css motion-reduce:transition-none', otwarta && 'rotate-180')} />
      </div>
      {otwarta && (
        <div id={trescId} className="space-y-3 pb-4">
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Kreska między sekcjami twardymi a miękkimi (plan §1.25): jedno zdanie o tym, że poniżej
 * nic nie odpada. Rysowana tylko w wariancie zwijanym (wyszukiwarka).
 */
function KreskaMiekkich() {
  const { t } = useTranslation();
  return (
    <div role="separator" className="flex items-center gap-2.5 pb-1.5 pt-3.5 text-xs text-muted-foreground">
      <span aria-hidden className="h-px flex-1 bg-border" />
      <span className="text-balance text-center">{t('search.filters.soft_divider')}</span>
      <span aria-hidden className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Akapit dymku ⓘ z pogrubieniami z tłumaczenia (`<b>znikają z listy</b>`). */
function AkapitInfo({ klucz }: { klucz: string }) {
  return (
    <p>
      <Trans i18nKey={klucz} components={{ b: <b className="font-semibold text-foreground" />, br: <br /> }} />
    </p>
  );
}

/**
 * Stan zwinięcia wierszy: domyślne z `domyslnieOtwarta`, pamiętane na sesję przeglądarki
 * (makieta, reguła 10). Czytamy raz przy montowaniu; każda zmiana trafia do sessionStorage.
 */
function useOtwarteSekcje(wlaczone: boolean) {
  const [otwarte, setOtwarte] = useState<Partial<Record<SekcjaPanelu, boolean>>>(() =>
    wlaczone ? wczytajOtwarte(typeof window === 'undefined' ? null : window.sessionStorage) : {},
  );
  const czyOtwarta = (id: SekcjaPanelu) => otwarte[id] ?? domyslnieOtwarta(id);
  const przelacz = (id: SekcjaPanelu) =>
    setOtwarte((s) => {
      const nast = { ...s, [id]: !(s[id] ?? domyslnieOtwarta(id)) };
      zapiszOtwarte(typeof window === 'undefined' ? null : window.sessionStorage, nast);
      return nast;
    });
  return { czyOtwarta, przelacz };
}

export function SearchFilters({ filters, onChange, pola, podpisy, zwijane = false }: Props) {
  const { t, i18n } = useTranslation();
  const photoSourceTags = usePhotoSourceTags();
  const widac = (...p: PoleFiltra[]) => !pola || p.some((x) => pola.includes(x));
  const { czyOtwarta, przelacz } = useOtwarteSekcje(zwijane);
  // Ramka diagnostyczna tylko dla administratora (bramka po stronie bazy: `flat.is_admin()`;
  // `dev_flags` nie ma ścieżki odczytu z przeglądarki, a A2 nie niesie migracji). Przed
  // startem znika w całości — docs/PreLaunchChecklist.md 2.6b.
  const { isAdmin } = useIsAdmin();
  const zwijanie = (id: SekcjaPanelu) =>
    zwijane ? { otwarta: czyOtwarta(id), przelacz: () => przelacz(id), podsumowanie: podsumowanieSekcji(id, filters, t, i18n.language) } : undefined;
  const info = (klucz: string, dodatkowe?: React.ReactNode) => ({
    etykieta: t('search.filters.section_info'),
    tresc: (
      <>
        <AkapitInfo klucz={klucz} />
        {dodatkowe}
      </>
    ),
  });

  const sekcjaMieszkania = widac('umeblowanie', 'pietro', 'kuchnia', 'lazienki', 'balkon', 'winda', 'internet', 'zwierze');

  return (
    <div className="divide-y-0">
      {/* LOKALIZACJA — wiersz panelu z tą samą kaskadą dzielnic i osiedli, co „Dzielnice” w polu
          wyszukiwania (plan §1.17, E2.2: jeden komponent, dwa wejścia). Startuje zwinięty: jego
          podsumowanie („Cała Warszawa” / „Mokotów, Muranów”) już mówi, gdzie szukasz. */}
      {widac('lokalizacja') && (
        <Sekcja id="lokalizacja" tytul={t('search.filters.location')} info={info('search.filters.location_hint')} zwijanie={zwijanie('lokalizacja')}>
          <LokalizacjaControl filters={filters} onChange={onChange} />
        </Sekcja>
      )}

      {widac('cena') && (
        // Definicje „Cena najmu / Koszt najmu” zostają pod ⓘ przy przełączniku (test
        // `cena-koszt-info`); ⓘ wiersza mówi tylko, co z listą robi zakres.
        <Sekcja id="cena" tytul={t('search.filters.price')} info={zwijane ? info('search.filters.info.price') : undefined} zwijanie={zwijanie('cena')}>
          <PriceControl filters={filters} onChange={onChange} bezEtykiety />
        </Sekcja>
      )}

      {widac('pokoje') && (
        <Sekcja id="pokoje" tytul={t('search.filters.rooms')} info={info(zwijane ? 'search.filters.info.rooms' : 'search.filters.coverage_hint')} zwijanie={zwijanie('pokoje')}>
          <RoomsControl filters={filters} onChange={onChange} bezEtykiety bezNotki />
        </Sekcja>
      )}

      {widac('metraz') && (
        zwijane ? (
          // W zwijanym wierszu wartość stoi w nagłówku wiersza (z filtra); w trakcie przeciągania
          // uchwyt pokazuje swój dymek, więc liczba nigdy nie jest pod palcem.
          <Sekcja id="metraz" tytul={t('search.filters.area')} info={info('search.filters.info.area')} zwijanie={zwijanie('metraz')}>
            <AreaControl filters={filters} onChange={onChange} bezEtykiety />
          </Sekcja>
        ) : (
          <Sekcja id="metraz">
            <AreaControl filters={filters} onChange={onChange} tytulSekcji={t('search.filters.area')} />
          </Sekcja>
        )
      )}

      {/* OKOLICA (twarda): metro i oceny okolicy — brak danych = oferta odpada. „Ze zwierzęciem”
          przeniosło się do Mieszkania (plan §1.15: to reguła mieszkania, nie okolicy). Profil
          pokazujący z tej sekcji samo zwierzę (Maciek 1.10) dostaje je więc w Mieszkaniu. */}
      {widac('metro', 'okolica') && (
      <Sekcja
        id="okolica"
        tytul={zwijane ? t('search.filters.section_neighbourhood') : t('search.filters.section_amenities')}
        // SRCH-03 → pod ⓘ (Piotr 29.09, „filtry 1-2-3”): co znaczą pastylki okolicy. W panelu
        // zostają same chipy; wyjaśnienie na żądanie, bo na telefonie nie ma najechania.
        info={{
          etykieta: t('search.filters.poi_legend_label'),
          tresc: (
            <>
            {zwijane ? <AkapitInfo klucz="search.filters.info.neighbourhood" /> : null}
            {widac('okolica') && (
            <ul data-testid="wyjasnienie-okolicy" className="space-y-1.5">
              <li><span className="inline-flex items-center gap-1 align-middle font-medium text-foreground"><WarsawMetroIcon size={12} />{t('search.filters.near_metro')}</span> — {t('search.filters.poi_explain.metro')}</li>
              {(['transit', 'walk', 'green'] as const).map((p) => {
                const Ikona = IKONA_FILTRA_POI[p];
                return (
                  <li key={p}><span className="inline-flex items-center gap-1 align-middle font-medium text-foreground"><Ikona className="h-3 w-3" aria-hidden />{t(`search.filters.poi_${p}`)}</span> — {t(`search.filters.poi_explain.${p}`)}</li>
                );
              })}
              {zwijane && <li><span className="font-medium text-foreground">{t('search.filters.blisko.title')}</span> — {t('search.filters.blisko.explain')}</li>}
            </ul>
            )}
            {!zwijane && <p>{t('search.filters.coverage_hint_neighbourhood')}</p>}
            </>
          ),
        }}
        zwijanie={zwijanie('okolica')}
      >
        <div className="flex flex-wrap gap-2">
          {widac('metro') && (
          <FilterChip
            active={filters.nearMetro ?? false}
            label={t('search.filters.near_metro')}
            ikona={<WarsawMetroIcon size={14} />}
            opis={t('search.filters.poi_explain.metro')}
            onToggle={(v) => onChange({ ...filters, nearMetro: v || undefined })}
          />
          )}
          {widac('okolica') && (['transit', 'walk', 'green'] as const).map((p) => {
            const active = filters.poi?.includes(p) ?? false;
            const Ikona = IKONA_FILTRA_POI[p];
            return (
              <FilterChip
                key={p}
                active={active}
                label={t(`search.filters.poi_${p}`)}
                ikona={<Ikona className="h-3.5 w-3.5" aria-hidden />}
                opis={t(`search.filters.poi_explain.${p}`)}
                onToggle={() => {
                  const current = filters.poi ?? [];
                  const next = active ? current.filter((x) => x !== p) : [...current, p];
                  onChange({ ...filters, poi: next.length ? next : undefined });
                }}
              />
            );
          })}
        </div>
        {/* „BLISKO DO…” (A10): tylko na stronie wyników — profil nie trzyma miejsca (plan E4.4). */}
        {zwijane && widac('okolica') && <BliskoDo filters={filters} onChange={onChange} />}
      </Sekcja>
      )}

      {/* OGŁOSZENIE (twarda): jaki rodzaj ogłoszenia — dawne „Dostępność” i „Zdjęcia” w jednym
          wierszu (makieta, wiersz 6). Chipy „Właściciel na FlatHero” (A5) i „Ulubione” (A6)
          dojdą na początek rzędu chipów. */}
      {widac('dostepnosc', 'zdjecia') && (
      <Sekcja
        id="ogloszenie"
        tytul={zwijane ? t('search.filters.section_listing') : t('search.filters.section_availability')}
        info={zwijane ? info('search.filters.info.listing', (
          <>
            <AkapitInfo klucz="search.filters.info.listing_owner" />
            <AkapitInfo klucz="search.filters.info.listing_favourites" />
            <AkapitInfo klucz="search.filters.info.listing_photos" />
          </>
        )) : undefined}
        zwijanie={zwijanie('ogloszenie')}
      >
        {widac('dostepnosc') && (
          <>
            {/* „Od zaraz" czyta tę samą parę dat co pastylka na kafelku (src/lib/availability.ts),
                więc filtr i karta nie mogą powiedzieć czegoś innego o tej samej ofercie. */}
            <div className="flex flex-wrap gap-2">
              {/* „Właściciel na FlatHero” (A5, §1.16): `origin <> 'scraped'` — twardo. */}
              <FilterChip
                active={filters.wlascicielNaFlatHero ?? false}
                label={t('search.filters.owner_on_flathero')}
                ikona={<UserRound className="h-3.5 w-3.5" aria-hidden />}
                opis={t('search.filters.owner_on_flathero_tip')}
                onToggle={(v) => onChange({ ...filters, wlascicielNaFlatHero: v || undefined })}
              />
              {/* „Ulubione” (A6, §1.14): filtr listy, nie kryterium — nie zapisuje się w Moich
                  filtrach i nie idzie do e-maili; reguła stoi pod ⓘ wiersza (makieta, Q1). */}
              <FilterChip
                active={filters.ulubione ?? false}
                label={t('search.filters.favourites')}
                ikona={<Heart className={cn('h-3.5 w-3.5', filters.ulubione && 'fill-current')} aria-hidden />}
                opis={t('search.filters.favourites_tip')}
                onToggle={(v) => onChange({ ...filters, ulubione: v || undefined, ulubioneIds: v ? filters.ulubioneIds : undefined })}
              />
              <FilterChip
                active={filters.availableNow ?? false}
                label={t('search.filters.available_now')}
                ikona={<IkonaOdZaraz size={14} />}
                opis={t('search.filters.available_now_tip')}
                onToggle={(v) => onChange({ ...filters, availableNow: v || undefined })}
              />
            </div>
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="posted" className="text-base md:text-sm">{t('search.filters.posted')}</Label>
              <Select
                value={String(filters.postedSinceHours ?? 0)}
                onValueChange={(v) =>
                  onChange({ ...filters, postedSinceHours: Number(v) === 0 ? null : Number(v) })
                }
              >
                <SelectTrigger id="posted" className="h-10 text-base focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring md:h-9 md:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSTED_OPTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`search.filters.posted_options.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {/* ZDJĘCIA (Piotr 29.09): oferty bez zdjęć stoją na końcu listy, za drugą kreską; ten
            przełącznik je ukrywa. Domyślnie WŁĄCZONY — wyłączony to filtr twardy na liście,
            pinach i liczniku (`bezZdjec === false`). */}
        {widac('zdjecia') && (
        <div className="flex items-start justify-between gap-4 pt-1">
          <Label htmlFor="bez-zdjec" className="cursor-pointer text-base font-normal leading-snug md:text-sm">
            {t('search.filters.show_without_photos')}
          </Label>
          <Switch
            id="bez-zdjec"
            data-testid="przelacznik-bez-zdjec"
            checked={filters.bezZdjec !== false}
            onCheckedChange={(v) => onChange({ ...filters, bezZdjec: v ? undefined : false })}
          />
        </div>
        )}
      </Sekcja>
      )}

      {/* KAUCJA — w grupie twardych (plan §1.25): znana wyższa kaucja wypada
          (`deposit_pln is null or <= X`); kaucję podaje ok. połowa ogłoszeń. */}
      {widac('kaucja') && (
      <Sekcja id="kaucja" tytul={t('search.filters.section_deposit')} info={info(zwijane ? 'search.filters.info.deposit' : 'search.filters.deposit_hint')} zwijanie={zwijanie('kaucja')}>
        <PoleKwoty
          etykieta={t('search.filters.deposit_max')}
          kwota={filters.kaucjaMax}
          podpowiedz={t('search.filters.deposit_placeholder')}
          jezyk={i18n.language}
          testId="kaucja-max"
          naZatwierdz={(v) => onChange({ ...filters, kaucjaMax: v ?? undefined })}
        />
      </Sekcja>
      )}

      {zwijane && (sekcjaMieszkania || widac('budynek', 'udogodnienia')) && <KreskaMiekkich />}

      {/* MIESZKANIE — filtry miękkie etapu 1b (ustalenia 28.09): spełnia → wyżej, nie wiadomo →
          niżej z adnotacją na karcie, wprost nie spełnia → wypada. Pojedynczy wybór w grupie;
          ponowne dotknięcie zaznaczonej opcji albo „Dowolne" zdejmuje filtr. Każda grupa ma
          „Dowolne” (plan §1.25). */}
      {sekcjaMieszkania && (
      <Sekcja
        id="mieszkanie"
        tytul={t('search.filters.section_flat')}
        info={info(zwijane ? 'search.filters.info.flat' : 'search.filters.soft_hint', widac('zwierze') ? <p>{t('search.filters.pets_soft_hint')}</p> : undefined)}
        zwijanie={zwijanie('mieszkanie')}
      >
        {widac('umeblowanie') && (
        <GrupaWyboru
          etykieta={t('search.filters.furnishing')}
          wartosc={filters.umeblowanie}
          opcje={[
            { v: 'umeblowane', label: t('search.filters.furnished_yes') },
            { v: 'nieumeblowane', label: t('search.filters.furnished_no') },
          ]}
          dowolne={t('search.filters.any_n')}
          naZmiane={(v) => onChange({ ...filters, umeblowanie: v })}
        />
        )}
        {/* Piętro: „Ostatnie" dojdzie, gdy agent danych da liczbę pięter budynku (typ `Pietro`
            w filtryMiekkie.ts) — bez pustej opcji w UI do tego czasu. */}
        {widac('pietro') && (
        <GrupaWyboru
          etykieta={t('search.filters.floor')}
          wartosc={filters.pietro}
          opcje={[{ v: 'parter', label: t('search.filters.floor_ground') }]}
          dowolne={t('search.filters.any_n')}
          naZmiane={(v) => onChange({ ...filters, pietro: v })}
        />
        )}
        {widac('kuchnia') && (
        <GrupaWyboru
          etykieta={t('search.filters.kitchen')}
          wartosc={filters.kuchnia}
          opcje={[
            { v: 'oddzielna', label: t('search.filters.kitchen_separate') },
            { v: 'aneks', label: t('search.filters.kitchen_annex') },
          ]}
          dowolne={t('search.filters.any_f')}
          naZmiane={(v) => onChange({ ...filters, kuchnia: v })}
        />
        )}
        {widac('lazienki') && (
        <GrupaWyboru
          etykieta={t('search.filters.bathrooms')}
          wartosc={filters.lazienki}
          opcje={[
            { v: '1', label: '1' },
            { v: '2+', label: '2+' },
          ]}
          dowolne={t('search.filters.any_adv')}
          naZmiane={(v) => onChange({ ...filters, lazienki: v })}
        />
        )}
        {/* Internet (Piotr i Maciek 28.09, bez „Brak"): światłowód albo dowolny. Miękko, jak balkon:
            oferta ze wzmianką wyżej, bez wzmianki niżej, nic nie odpada (filtryMiekkie.ts). */}
        {widac('internet') && (
        <GrupaWyboru
          etykieta={t('search.filters.internet')}
          wartosc={filters.internet}
          opcje={[{ v: 'swiatlowod', label: t('search.filters.internet_fibre') }]}
          dowolne={t('search.filters.any_m')}
          naZmiane={(v) => onChange({ ...filters, internet: v })}
        />
        )}
        {/* DODATKOWO (plan §1.25): balkon i winda (1b cz. 2) oraz „Ze zwierzęciem” (§1.15).
            Balkon ma DWA stany — milczenie ogłoszenia to „nie wiadomo", nigdy „nie" — więc tylko
            porządkuje; winda odcina jawne „bez windy" (`has_elevator is not false`); zwierzę
            miękko: deklaracja wyżej, brak deklaracji niżej. */}
        {widac('balkon', 'winda', 'zwierze') && (
        <div className="space-y-1.5 pt-1" role="group" aria-label={t('search.filters.extras_label')}>
          {zwijane && <p className="text-base font-medium text-foreground md:text-sm">{t('search.filters.extras_label')}</p>}
          <div className="flex flex-wrap gap-2">
            {widac('balkon') && (
            <FilterChip
              active={filters.balkon ?? false}
              label={t('search.filters.balcony')}
              opis={t('search.filters.balcony_tip')}
              onToggle={(v) => onChange({ ...filters, balkon: v || undefined })}
            />
            )}
            {widac('winda') && (
            <FilterChip
              active={filters.winda ?? false}
              label={t('search.filters.elevator')}
              opis={t('search.filters.elevator_tip')}
              onToggle={(v) => onChange({ ...filters, winda: v || undefined })}
            />
            )}
            {widac('zwierze') && (
            <FilterChip
              active={filters.zeZwierzeciem ?? false}
              // Etykieta „Ze zwierzęciem", nie „Przyjazne zwierzętom": filtr jest miękki (zostają
              // oferty bez deklaracji), więc nie obiecujemy, że każda pozwala na zwierzę.
              label={t('search.pets_chip')}
              ikona={<IkonaZwierzat size={14} />}
              opis={t('search.filters.pets_soft_hint')}
              onToggle={(v) => onChange({ ...filters, zeZwierzeciem: v || undefined })}
            />
            )}
          </div>
          {widac('zwierze') && podpisy?.zwierze && (
            <p className="text-xs text-muted-foreground">{podpisy.zwierze}</p>
          )}
        </div>
        )}
      </Sekcja>
      )}

      {/* BUDYNEK (plan E3.2/E3.3) — miękko, jak Mieszkanie: pasujący typ wyżej, brak danych niżej,
          jawnie inny typ odpada (`predykatyWykluczenia`). Słownik ogłoszeń, nie profilu (S2). */}
      {widac('budynek') && (
      <Sekcja id="budynek" tytul={t('search.filters.section_building')} info={info(zwijane ? 'search.filters.info.building' : 'search.filters.building_hint')} zwijanie={zwijanie('budynek')}>
        <GrupaWyboru
          etykieta={t('search.filters.building_type')}
          wartosc={filters.typBudynku}
          opcje={TYPY_BUDYNKU.map((v) => ({ v, label: t(`search.filters.building_type_value.${v}`) }))}
          dowolne={t('search.filters.any_m')}
          naZmiane={(v) => onChange({ ...filters, typBudynku: v })}
        />
        <GrupaWyboru
          etykieta={t('search.filters.building_age')}
          wartosc={filters.wiekBudynku}
          opcje={WIEKI_BUDYNKU.map((v) => ({ v, label: t(`search.filters.building_age_value.${v}`) }))}
          dowolne={t('search.filters.any_m')}
          naZmiane={(v) => onChange({ ...filters, wiekBudynku: v })}
        />
        {/* Bez stałej linii podpowiedzi (Maciek 20.10, S4): żaden inny wiersz panelu jej nie ma; ten
            sam tekst jest pod (i) przy tytule sekcji. */}
      </Sekcja>
      )}

      {/* UDOGODNIENIA W MIESZKANIU (1b cz. 2, Piotr 28.09: miękko). Nic nie odpada — ogłoszenia
          zapisują tylko „jest", więc brak wzmianki to „nie wiadomo". Kolejność: pokrycie. */}
      {widac('udogodnienia') && (
      <Sekcja id="udogodnienia" tytul={t('search.filters.section_in_flat')} info={info(zwijane ? 'search.filters.info.amenities' : 'search.filters.in_flat_hint')} zwijanie={zwijanie('udogodnienia')}>
        <div className="flex flex-wrap gap-2">
          {UDOGODNIENIA.map((u) => {
            const aktywne = filters.udogodnienia?.includes(u) ?? false;
            return (
              <FilterChip
                key={u}
                active={aktywne}
                label={t(`search.filters.amenity.${u}`)}
                onToggle={() => {
                  const obecne = filters.udogodnienia ?? [];
                  const nowe = aktywne ? obecne.filter((x) => x !== u) : [...obecne, u];
                  // Zdjęcie parkingu zdejmuje też jego podfiltr „w garażu podziemnym”.
                  const garaz = u === 'parking' && aktywne ? undefined : filters.garaz;
                  onChange({ ...filters, udogodnienia: nowe.length ? nowe : undefined, garaz });
                }}
              />
            );
          })}
        </div>
        {/* PODFILTR „W GARAŻU PODZIEMNYM” (Piotr 6.10, wrócił 18.10) pod „Parking lub garaż”:
            widoczny po włączeniu parkingu. Miękko, token `garage` (parking podziemny z treści). */}
        {filters.udogodnienia?.includes('parking') && (
          <div className="mt-2 flex flex-wrap gap-2 border-l-2 border-primary/30 pl-3" data-testid="podfiltr-garazu">
            <FilterChip
              active={filters.garaz ?? false}
              label={t('search.filters.garage_underground')}
              onToggle={(v) => onChange({ ...filters, garaz: v || undefined })}
            />
          </div>
        )}
      </Sekcja>
      )}

      {/* Debug / Dev — internal-only controls, slated for removal before public
          launch. Kept visually quarantined (dashed box) so they're easy to find and rip out.
          Ostatni wiersz panelu, zwinięty (plan §1.19). */}
      {/* `data-tylko-dev`: kontrole jakości (np. scripts/shot-steps/cele-dotyku.mjs) pomijają tę
          sekcję — nie trafi do użytkowników (docs/PreLaunchChecklist.md). */}
      {widac('debug') && isAdmin && (
      <div data-tylko-dev>
      <Sekcja
        id="debug"
        tytul={t('search.filters.debug_section')}
        zwijanie={zwijanie('debug')}
      >
        {/* PRZEŁĄCZNIKA „pokaż też agencje" TU NIE MA (decyzja Piotra 27.09). Wyniki
            pokazują ogłoszenia prywatne i nierozstrzygnięte — `DEFAULT_FILTERS` — a
            odkrywanie agencji wraca dopiero po decyzji produktowej, nie jako przełącznik
            w sekcji debugowej. Model filtra (`postingTypes`) ZOSTAJE: czyta go zapytanie
            listy i histogram, a backoffice dalej potrzebuje tego wymiaru. Plakietka
            „Agencja" na karcie i na stronie oferty też zostaje — to prawdziwa informacja
            o ofercie, do której ktoś trafi linkiem. */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="debug_photo_sources"
            checked={photoSourceTags}
            onCheckedChange={(checked) => setPhotoSourceTags(checked === true)}
          />
          <Label htmlFor="debug_photo_sources" className="cursor-pointer text-sm font-normal">
            {t('search.filters.debug_photo_sources')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="debug_include_dead"
            checked={filters.includeDead ?? false}
            onCheckedChange={(checked) => onChange({ ...filters, includeDead: checked ? true : undefined })}
          />
          <Label htmlFor="debug_include_dead" className="cursor-pointer text-sm font-normal">
            {t('search.filters.debug_include_dead')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="debug_no_price"
            checked={filters.noPrice ?? false}
            onCheckedChange={(checked) => onChange({ ...filters, noPrice: checked ? true : undefined })}
          />
          <Label htmlFor="debug_no_price" className="cursor-pointer text-sm font-normal">
            {t('search.filters.debug_no_price')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="debug_approx_location"
            checked={filters.approxLocation ?? false}
            onCheckedChange={(checked) => onChange({ ...filters, approxLocation: checked ? true : undefined })}
          />
          <Label htmlFor="debug_approx_location" className="cursor-pointer text-sm font-normal">
            {t('search.filters.debug_approx_location')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="debug_sanity_outliers"
            checked={filters.sanityOutliers ?? false}
            onCheckedChange={(checked) => onChange({ ...filters, sanityOutliers: checked ? true : undefined })}
          />
          <Label htmlFor="debug_sanity_outliers" className="cursor-pointer text-sm font-normal">
            {t('search.filters.debug_sanity_outliers')}
          </Label>
        </div>
      </Sekcja>
      </div>
      )}
    </div>
  );
}

/**
 * Grupa pojedynczego wyboru dla filtra miękkiego: zaznaczenie opcji ustawia filtr, ponowne
 * dotknięcie tej samej opcji (albo „Dowolne", gdy grupa je ma) go zdejmuje. `undefined` =
 * filtr nieaktywny, jak w każdym innym polu `SourcePostFilters`.
 */
function GrupaWyboru<T extends string>({
  etykieta,
  wartosc,
  opcje,
  dowolne,
  naZmiane,
}: {
  etykieta: string;
  wartosc: T | undefined;
  opcje: { v: T; label: string }[];
  /** Jawna opcja „bez znaczenia" — tam, gdzie ustalenia ją wymieniają (piętro, kuchnia). */
  dowolne?: string;
  naZmiane: (v: T | undefined) => void;
}) {
  return (
    <div className="space-y-1.5" role="group" aria-label={etykieta}>
      <p className="text-base font-medium text-foreground md:text-sm">{etykieta}</p>
      <div className="flex flex-wrap gap-2">
        {/* „Dowolne” zawsze PIERWSZE, jak w Pokojach i w makiecie (`grp()`: any pill first). */}
        {dowolne && <FilterChip active={wartosc === undefined} label={dowolne} onToggle={() => naZmiane(undefined)} />}
        {opcje.map((o) => (
          <FilterChip key={o.v} active={wartosc === o.v} label={o.label} onToggle={(on) => naZmiane(on ? o.v : undefined)} />
        ))}
      </div>
    </div>
  );
}

function FilterChip(props: {
  active: boolean;
  label: string;
  onToggle: (next: boolean) => void;
  /** Ikona przed napisem (np. znak metra) — zamiast emoji w samym napisie. */
  ikona?: React.ReactNode;
  /**
   * Jedno zdanie „co ten filtr sprawdza” w dymku po najechaniu (pulpit). Na telefonie dymka nie
   * ma — dotknięcie przełącza filtr, a przytrzymanie gryzłoby się z przewijaniem panelu — więc tam
   * te same zdania stoją pod ⓘ przy tytule sekcji.
   */
  opis?: string;
}) {
  const przycisk = (
    <button
      type="button"
      aria-pressed={props.active}
      onClick={() => props.onToggle(!props.active)}
      // Obrys fokusu TYLKO przy klawiaturze i z tokenu `ring` (Piotr 29.09: „Telewizor” po
      // kliknięciu miał niebieski obrys przeglądarki).
      // JEDEN komponent wyboru w całym panelu (Piotr 29.09: pokoje były prostokątami w siatce,
      // reszta pigułkami — dwa kształty dla tej samej roli). Wysokość 40 px na telefonie (cel
      // dotyku), tekst jak w polach: 16 px na telefonie (iOS), 14 px na pulpicie.
      className={`inline-flex h-10 min-w-[2.5rem] items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-base font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring md:h-9 md:px-3 md:text-sm ${
        props.active
          ? 'border-primary bg-primary/5 text-foreground'
          : 'text-muted-foreground hover:border-foreground/30'
      }`}
    >
      {props.ikona}
      {props.label}
    </button>
  );
  if (!props.opis) return przycisk;
  return (
    // Własny dostawca: panel filtrów renderuje się też poza drzewem App (testy, podglądy).
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{przycisk}</TooltipTrigger>
        <TooltipContent side="top" align="start" collisionPadding={12} className="max-w-64 text-balance">{props.opis.charAt(0).toUpperCase() + props.opis.slice(1)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

